import React from 'react';
import clsx from 'clsx';
import type { Conversation } from '../../stores/whatsapp/chatStore';
import Avatar from './WaAvatar';
import { formatRelativeTime, truncateText } from '../../services/whatsapp/waFormatters';
import { useDeptVisibilityStore } from '../../stores/whatsapp/deptVisibilityStore';
import { useAuth } from '../../providers/auth';

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}

export default function ConversationItem({
  conversation,
  isActive,
  onClick,
}: ConversationItemProps) {
  const { user } = useAuth();
  const userDept = user?.departamento?.nome || '';
  const hasUnread = conversation.unread_count > 0;
  const isVip = conversation.client_priority === 'vip' || conversation.client_priority === 'high';
  const canSeeDeptStore = useDeptVisibilityStore(s => s.canSeeDept);
  const deptRestricted = useDeptVisibilityStore(s => s.restrictByDept);
  const convDept = conversation.assigned_department || conversation.department;

  // Verifica visibilidade: store + fallback local por departamento do usuario
  const isVisible = (() => {
    if (!deptRestricted) return true;
    if (!convDept) return true;
    if (canSeeDeptStore(convDept)) return true;
    // Fallback: comparacao direta com grupo do usuario
    if (!userDept) return true;
    const u = userDept.toLowerCase().trim();
    const d = convDept.toLowerCase().trim();
    if (u === d) return true;
    if (u.includes(d) || d.includes(u)) return true;
    const uWords = u.split(/[\s.,-]+/);
    const dWords = d.split(/[\s.,-]+/);
    if (uWords[0] === dWords[0]) return true;
    return false;
  })();
  const isOtherDept = !isVisible;

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'active-scale flex w-full items-center gap-3 px-4 py-3 text-left transition-all duration-200',
        'border-b border-gray-100/60',
        isActive
          ? 'bg-wa-500/8 border-l-2 border-l-wa-500'
          : 'border-l-2 border-l-transparent hover:bg-gray-50/80',
      )}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <Avatar name={conversation.client_name} size="md" />
        {/* Priority dot indicator */}
        {conversation.priority === 'urgent' && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-wa-accent-red" />
        )}
        {conversation.priority === 'high' && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-wa-accent-orange" />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span
            className={clsx(
              'truncate text-sm',
              hasUnread ? 'text-header font-semibold' : 'font-medium text-gray-700',
            )}
          >
            {conversation.client_name}
          </span>
          {isVip && (
            <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-400/20">
              <span className="text-[9px]">👑</span>
            </span>
          )}
        </div>
        {conversation.client_company && (
          <p className="mt-px truncate font-mono text-[10px] tracking-tight text-gray-400/80">
            {conversation.client_id && (
              <span className="text-gray-300">
                #{conversation.client_id.replace(/[^0-9]/g, '').slice(0, 4)}{' '}
              </span>
            )}
            {conversation.client_company}
          </p>
        )}
        <p
          className={clsx(
            'mt-0.5 truncate text-xs',
            hasUnread ? 'font-medium text-gray-600' : 'text-gray-400',
            isOtherDept && 'select-none',
          )}
          style={isOtherDept ? { filter: 'blur(4px)' } : undefined}
        >
          {(() => {
            if (isOtherDept) return 'Mensagem de outro departamento';
            if (conversation.last_message) return truncateText(conversation.last_message, 45);
            return 'Sem mensagens';
          })()}
        </p>
        {isOtherDept && convDept && (
          <p className="mt-0.5 text-[10px] italic text-gray-400">{convDept}</p>
        )}
      </div>

      {/* Meta (time + unread badge) */}
      <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
        <span
          className={clsx('text-[11px]', hasUnread ? 'font-semibold text-wa-500' : 'text-gray-400')}
        >
          {conversation.last_message_at ? formatRelativeTime(conversation.last_message_at) : ''}
        </span>
        {hasUnread ? (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-wa-500 px-1.5 text-[11px] font-bold text-white">
            {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
          </span>
        ) : (
          /* Spacer to keep alignment consistent */
          <span className="h-5" />
        )}
      </div>
    </button>
  );
}
